import { normalizzaSigla } from "@app/schema";
import type { ChecklistGara, Gare, GaraFields, Lotti, LottoFields, ModelloChecklist, ServiziGara, ServizioFields, StatoGara } from "@app/schema";
import { sanitizeFolderName, type FieldsPatch, type ListItem, type ListRepository } from "@app/sp-data";
import { eliminaChecklist } from "./checklist";

/**
 * Logica applicativa delle gare: le pagine chiamano queste funzioni, non i repository
 * direttamente. Nessuna dipendenza da React o dalla configurazione: si può testare
 * con il Graph simulato.
 */

export type Gara = ListItem<GaraFields>;
export type Lotto = ListItem<LottoFields>;
export type Servizio = ListItem<ServizioFields>;

export interface GareRepos {
  gare: ListRepository<typeof Gare>;
  lotti: ListRepository<typeof Lotti>;
  servizi: ListRepository<typeof ServiziGara>;
  checklist: ListRepository<typeof ChecklistGara>;
  modelloChecklist: ListRepository<typeof ModelloChecklist>;
  /** Numero = ID dell'elemento + base (base 999 → la prima gara è OA1000). */
  numeroBase: number;
}

/* ------------------------------- Codici e nomi ------------------------------- */

/** OA + numero a 4 cifre: OA1000. */
export const prefissoOA = (numero: number) => `OA${String(numero).padStart(4, "0")}`;

/** Codici del formato completo (OA1000_ASS_03-10-2026); quelli brevi (OA1000) sono delle prime prove. */
const CODICE_COMPLETO = /^OA\d{4}_.+_\d{2}-\d{2}-\d{4}(_ALBO)?$/;
export const codiceCompleto = (codice?: string) => !!codice && CODICE_COMPLETO.test(codice);

const dataItaliana = new Intl.DateTimeFormat("it-IT", { timeZone: "Europe/Rome", day: "2-digit", month: "2-digit", year: "numeric" });

/** gg-mm-aaaa, nel fuso orario italiano. */
export function dataPerNome(iso: string): string {
  const parts = Object.fromEntries(dataItaliana.formatToParts(new Date(iso)).map((p) => [p.type, p.value]));
  return `${parts.day}-${parts.month}-${parts.year}`;
}

/**
 * Codice della gara: OA1000_ASS_03-10-2026 (numero, sigla del servizio, data di
 * inserimento nel fuso italiano). È anche il nome della cartella di gara: la data è
 * quella di inserimento, quindi il nome non cambia se la cartella si crea giorni dopo.
 */
export function codiceGara(p: { numero: number; sigla: string; inserita: string; albo?: boolean }): string {
  return sanitizeFolderName(`${prefissoOA(p.numero)}_${p.sigla.trim()}_${dataPerNome(p.inserita)}${p.albo ? "_ALBO" : ""}`);
}

/* ------------------------------- Albi ------------------------------- */

/** Iscrizione a un albo (stesso elenco delle gare, Tipo = Albo; vuoto = gara). */
export const eAlbo = (g: Gara) => g.fields.Tipo === "Albo";

export type StatoIscrizione = "Da iscrivere" | "Iscritta" | "In scadenza" | "Scaduta";

/** Giorni prima della scadenza dell'iscrizione in cui l'albo è "in scadenza". */
export const PREAVVISO_ALBI = 30;

const giornoRoma = new Intl.DateTimeFormat("en-CA", { timeZone: "Europe/Rome", year: "numeric", month: "2-digit", day: "2-digit" });
/** Data del calendario italiano (aaaa-mm-gg) di un istante. */
export const giornoItaliano = (iso: string | Date) => giornoRoma.format(typeof iso === "string" ? new Date(iso) : iso);
/** Data scelta (aaaa-mm-gg) → valore per una colonna "solo data": mezzogiorno UTC, stesso giorno in Italia. */
export const dataSoloGiorno = (giorno: string) => (giorno ? `${giorno}T12:00:00Z` : undefined);

/** Giorni di calendario alla scadenza dell'iscrizione (0 = oggi, negativo = scaduta). */
export function giorniAlRinnovo(g: Gara, now = new Date()): number | undefined {
  if (!g.fields.ScadenzaIscrizione) return undefined;
  const a = Date.parse(`${giornoItaliano(g.fields.ScadenzaIscrizione)}T00:00:00Z`);
  const b = Date.parse(`${giornoItaliano(now)}T00:00:00Z`);
  return Math.round((a - b) / 86_400_000);
}

export function statoIscrizione(g: Gara, now = new Date()): StatoIscrizione {
  if (!g.fields.DataIscrizione) return "Da iscrivere";
  const giorni = giorniAlRinnovo(g, now);
  if (giorni === undefined) return "Iscritta";
  if (giorni < 0) return "Scaduta";
  return giorni <= PREAVVISO_ALBI ? "In scadenza" : "Iscritta";
}

/** Albi: prima quelli da rinnovare (scadenza più vicina), poi senza scadenza, dal più recente. */
export function ordinaAlbi(items: Gara[]): Gara[] {
  const t = (g: Gara) => (g.fields.ScadenzaIscrizione ? Date.parse(g.fields.ScadenzaIscrizione) : Infinity);
  return [...items].sort((a, b) => t(a) - t(b) || b.id - a.id);
}

/** Nome della cartella: il codice della gara, con _L2 per il lotto 2. */
export const nomeCartella = (codice: string, lotto?: number) => sanitizeFolderName(`${codice}${lotto ? `_L${lotto}` : ""}`);

/** Cartelle di gara riconoscibili dal nome (anche quelle create dalla vecchia app). */
export const NOME_CARTELLA_GARA = /^OA\d{4}([_ -]|$)/i;

/* ------------------------------- Oggetto e lunghezze ------------------------------- */

/** Limite di SharePoint per Title e per le colonne di testo su una riga. */
export const MAX_TESTO = 255;

/** Oggetto per intero: quello lungo sta in OggettoCompleto, il Titolo ne ha l'inizio. */
export const oggettoGara = (g: Gara) => g.fields.OggettoCompleto || g.fields.Title || "";

/** Titolo (max 255) e testo integrale, se più lungo. */
export function campiOggetto(testo: string): { Title: string; OggettoCompleto: string | null } {
  const t = testo.trim().replace(/\s+/g, " ");
  return t.length <= MAX_TESTO ? { Title: t, OggettoCompleto: null } : { Title: `${t.slice(0, MAX_TESTO - 1).trimEnd()}…`, OggettoCompleto: testo.trim() };
}

/** Errore leggibile se un testo supera la lunghezza della colonna (SharePoint risponde solo "Invalid request"). */
export function controllaLunghezze(campi: Array<[etichetta: string, valore: string | undefined | null, max?: number]>): void {
  for (const [etichetta, valore, max = MAX_TESTO] of campi)
    if (valore && valore.length > max) throw new Error(`"${etichetta}" è troppo lungo: ${valore.length} caratteri, al massimo ${max}.`);
}

function controllaGara(dati: Partial<DatiGara>) {
  controllaLunghezze([
    ["Stazione appaltante", dati.Stazione],
    ["CIG", dati.CIG, 40],
    ["Altri componenti RTI", dati.Mandanti],
  ]);
}

/* ------------------------------- Stato e scadenze ------------------------------- */

export type StatoVisuale = "Attiva" | "Scaduta" | "Archiviata";

export function statoVisuale(g: Gara, now = new Date()): StatoVisuale {
  if (g.fields.Stato === "Archiviata") return "Archiviata";
  if (eAlbo(g)) return "Attiva"; // gli albi non hanno scadenza di presentazione
  if (g.fields.Scadenza && new Date(g.fields.Scadenza) < now) return "Scaduta";
  return "Attiva";
}

/** Giorni di calendario alla scadenza (0 = oggi, negativo = scaduta). */
export function giorniAllaScadenza(g: Gara, now = new Date()): number | undefined {
  if (!g.fields.Scadenza) return undefined;
  const d = new Date(g.fields.Scadenza);
  const a = Date.UTC(d.getFullYear(), d.getMonth(), d.getDate());
  const b = Date.UTC(now.getFullYear(), now.getMonth(), now.getDate());
  return Math.round((a - b) / 86_400_000);
}

/** Attive: scadenza più vicina prima (senza scadenza in fondo). Le altre: dalla più recente. */
export function ordinaGare(items: Gara[]): Gara[] {
  const t = (g: Gara) => (g.fields.Scadenza ? new Date(g.fields.Scadenza).getTime() : undefined);
  return [...items].sort((a, b) => {
    const ta = t(a);
    const tb = t(b);
    if (statoVisuale(a) === "Attiva" && statoVisuale(b) === "Attiva") return (ta ?? Infinity) - (tb ?? Infinity) || b.id - a.id;
    return (tb ?? 0) - (ta ?? 0) || b.id - a.id;
  });
}

/** Testo in cui cercare (codice, oggetto, stazione, CIG). */
export const testoRicerca = (g: Gara, sigla?: string) =>
  [g.fields.Codice, oggettoGara(g), g.fields.Stazione, g.fields.CIG, sigla].filter(Boolean).join(" ").toLowerCase();

/* ------------------------------- Lettura ------------------------------- */

/** Gare non archiviate (tutte: l'elenco resta piccolo) oppure archiviate. */
export function loadGare(r: GareRepos, archiviate = false): Promise<Gara[]> {
  return r.gare.queryAll({
    filter: `fields/Stato eq '${archiviate ? "Archiviata" : "Attiva"}'`,
    orderBy: { field: "Created", desc: true },
    top: 500,
    max: archiviate ? 500 : undefined,
    allowNonIndexed: true,
  });
}

export async function loadLotti(r: GareRepos, garaId: number): Promise<Lotto[]> {
  const items = await r.lotti.queryAll({ filter: `fields/GaraLookupId eq ${garaId}`, top: 200, allowNonIndexed: true });
  return items.sort((a, b) => (a.fields.Numero ?? 0) - (b.fields.Numero ?? 0));
}

export async function loadServizi(r: GareRepos): Promise<Servizio[]> {
  const items = await r.servizi.queryAll({ top: 500 });
  return items.sort((a, b) => (a.fields.Title ?? "").localeCompare(b.fields.Title ?? "", "it"));
}

/* ------------------------------- Scrittura ------------------------------- */

export type DatiGara = Pick<
  GaraFields,
  | "Title"
  | "Stazione"
  | "Servizio"
  | "CIG"
  | "Importo"
  | "Scadenza"
  | "ScadenzaInteresse"
  | "AperturaBuste"
  | "LinkBando"
  | "PerLotti"
  | "Mandanti"
  | "Valutazione"
  | "NoteValutazione"
  | "Esito"
  | "ImportoAggiudicato"
  | "TipoEnte"
  | "Tipo"
  | "DataIscrizione"
  | "ScadenzaIscrizione"
>;

/**
 * Crea la gara e le assegna numero e codice (OA1000_ASS_03-10-2026). Il numero deriva
 * dall'ID che SharePoint assegna all'elemento: nessuna collisione tra inserimenti simultanei.
 */
export async function creaGara(r: GareRepos, dati: DatiGara): Promise<Gara> {
  const servizio = dati.Servizio ? await r.servizi.get(dati.Servizio) : undefined;
  if (!servizio?.fields.Sigla) throw new Error("Il servizio scelto non ha una sigla.");
  if (!dati.Title?.trim()) throw new Error("L'oggetto è obbligatorio.");
  controllaGara(dati);
  const oggetto = campiOggetto(dati.Title);
  dati = { ...dati, Title: oggetto.Title };
  const albo = dati.Tipo === "Albo";
  // Un albo non ha lotti, scadenza di presentazione, valutazione né esito
  const campi = albo ? { ...dati, PerLotti: false, Scadenza: undefined, Valutazione: undefined, Esito: undefined, ImportoAggiudicato: undefined } : dati;
  const item = await r.gare.create({ ...campi, ...(oggetto.OggettoCompleto ? { OggettoCompleto: oggetto.OggettoCompleto } : {}), Tipo: albo ? "Albo" : "Gara", Stato: "Attiva" });
  const numero = item.id + r.numeroBase;
  await r.gare.update(item.id, { Numero: numero, Codice: codiceGara({ numero, sigla: servizio.fields.Sigla, inserita: item.createdAt, albo }) });
  return (await r.gare.get(item.id))!;
}

/**
 * Aggiorna i dati della gara (con l'ETag: se qualcuno l'ha modificata nel frattempo,
 * GraphError.isConflict). Finché le cartelle non esistono il codice segue il servizio
 * scelto; dopo resta fisso, perché è il nome delle cartelle.
 */
export async function salvaGara(r: GareRepos, g: Gara, dati: Partial<DatiGara>): Promise<void> {
  const patch: FieldsPatch<GaraFields> = {};
  // Il tipo (gara o albo) non cambia dopo la creazione: è nel codice
  const { Tipo: _tipo, ...resto } = dati;
  void _tipo;
  dati = resto;
  controllaGara(dati);
  if (dati.Title !== undefined) {
    if (!dati.Title.trim()) throw new Error("L'oggetto è obbligatorio.");
    const oggetto = campiOggetto(dati.Title);
    dati = { ...dati, Title: oggetto.Title };
    patch.OggettoCompleto = oggetto.OggettoCompleto;
  }
  for (const [k, v] of Object.entries(dati)) (patch as Record<string, unknown>)[k] = v === undefined || v === "" ? null : v;
  if (!g.fields.PosizioneId && !g.fields.CartellaId) {
    const servizioId = dati.Servizio ?? g.fields.Servizio;
    const servizio = servizioId ? await r.servizi.get(servizioId) : undefined;
    if (servizio?.fields.Sigla) {
      const codice = codiceGara({ numero: g.fields.Numero ?? g.id + r.numeroBase, sigla: servizio.fields.Sigla, inserita: g.createdAt, albo: eAlbo(g) });
      if (codice !== g.fields.Codice) patch.Codice = codice;
    }
  }
  await r.gare.update(g.id, patch, g.etag);
}

export const haCartelle = (g: Gara) => !!g.fields.CartellaId;
/** Creazione avviata ma non conclusa: va ripresa nella stessa posizione. */
export const creazioneInterrotta = (g: Gara) => !!g.fields.PosizioneId && !g.fields.CartellaId;
/** Gara a lotti con cartelle già create e lotti aggiunti dopo, ancora senza cartella. */
export const lottiSenzaCartella = (g: Gara, lotti: Lotto[]) => (g.fields.PerLotti ? lotti.filter((l) => !l.fields.CartellaId) : []);

export async function cambiaStato(r: GareRepos, g: Gara, stato: StatoGara): Promise<void> {
  await r.gare.update(g.id, { Stato: stato }, g.etag);
}

/** Elimina la gara e i suoi lotti. Le gare con cartelle non si eliminano: si archiviano. */
export async function eliminaGara(r: GareRepos, g: Gara, lotti: Lotto[]): Promise<void> {
  if (haCartelle(g) || g.fields.PosizioneId || lotti.some((l) => l.fields.CartellaId))
    throw new Error("La gara ha già delle cartelle: non si può eliminare. Archiviala.");
  await eliminaChecklist(r, g.id);
  for (const l of lotti) await r.lotti.delete(l.id);
  await r.gare.delete(g.id, g.etag);
}

export type DatiLotto = Pick<LottoFields, "Title" | "Numero" | "CIG" | "Importo" | "Esito" | "ImportoAggiudicato">;

export async function salvaLotto(r: GareRepos, g: Gara, dati: DatiLotto, esistente?: Lotto, altri: Lotto[] = []): Promise<void> {
  if (!dati.Numero || dati.Numero < 1) throw new Error("Il numero del lotto deve essere 1 o più.");
  controllaLunghezze([
    ["Oggetto del lotto", dati.Title?.trim()],
    ["CIG", dati.CIG, 40],
  ]);
  if (altri.some((l) => l.id !== esistente?.id && l.fields.Numero === dati.Numero)) throw new Error(`Esiste già il lotto ${dati.Numero}.`);
  if (esistente?.fields.CartellaId && esistente.fields.Numero !== dati.Numero)
    throw new Error("La cartella del lotto è già stata creata: il numero non si può più cambiare.");
  const campi = {
    Title: dati.Title?.trim() || `Lotto ${dati.Numero}`,
    Numero: dati.Numero,
    CIG: dati.CIG || null,
    Importo: dati.Importo ?? null,
    Esito: dati.Esito ?? null,
    ImportoAggiudicato: dati.Esito === "Aggiudicata" ? (dati.ImportoAggiudicato ?? null) : null,
  };
  if (esistente) await r.lotti.update(esistente.id, campi, esistente.etag);
  else
    await r.lotti.create({
      Title: campi.Title,
      Numero: campi.Numero,
      CIG: campi.CIG ?? undefined,
      Importo: campi.Importo ?? undefined,
      Esito: campi.Esito ?? undefined,
      ImportoAggiudicato: campi.ImportoAggiudicato ?? undefined,
      Gara: g.id,
    });
}

export async function eliminaLotto(r: GareRepos, l: Lotto): Promise<void> {
  if (l.fields.CartellaId) throw new Error("La cartella del lotto è già stata creata: il lotto non si può eliminare.");
  const g = l.fields.Gara;
  if (g) await eliminaChecklist(r, g, l);
  await r.lotti.delete(l.id, l.etag);
}

/**
 * Servizi: niente eliminazione (sono collegati alle gare), solo disattivazione.
 * La sigla si normalizza (spazi e _ diventano -); sigle ripetute sono ammesse.
 */
export async function salvaServizio(r: GareRepos, dati: Pick<ServizioFields, "Title" | "Sigla" | "Tipologia" | "Attivo">, esistente?: Servizio) {
  const sigla = normalizzaSigla(dati.Sigla ?? "");
  if (!dati.Title?.trim() || !sigla) throw new Error("Servizio e sigla sono obbligatori.");
  controllaLunghezze([["Servizio", dati.Title.trim()], ["Tipologia", dati.Tipologia?.trim()]]);
  if (sigla.length > 50) throw new Error("La sigla può avere al massimo 50 caratteri.");
  const campi = { Title: dati.Title.trim(), Sigla: sigla, Tipologia: dati.Tipologia?.trim() || undefined, Attivo: dati.Attivo ?? true };
  if (esistente) await r.servizi.update(esistente.id, { ...campi, Tipologia: campi.Tipologia ?? null }, esistente.etag);
  else await r.servizi.create(campi);
}

/** Riepilogo degli esiti dei lotti, per la scheda di una gara a lotti. */
export function esitoLotti(lotti: Lotto[]): string | undefined {
  const noti = lotti.filter((l) => l.fields.Esito);
  if (!noti.length) return undefined;
  const vinti = noti.filter((l) => l.fields.Esito === "Aggiudicata").length;
  if (vinti === 0) return noti.length === lotti.length ? "Nessun lotto aggiudicato" : undefined;
  return `${vinti} ${vinti === 1 ? "lotto aggiudicato" : "lotti aggiudicati"} su ${lotti.length}`;
}
